import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Clock, Trash2, Eye } from 'lucide-react'
import { api } from '../../api/client'
import { Select } from '../../components/ui/Input'
import { fmtDateTime } from '../../utils/date'
import Modal from '../../components/ui/Modal'
import Pagination from '../../components/ui/Pagination'
import EmptyState from '../../components/ui/EmptyState'
import LoadingState from '../../components/ui/LoadingState'
import { useConfirm } from '../../components/ui/ConfirmDialog'

const TOOL_OPTIONS = [
  { value: '', label: 'All Tools' },
  { value: 'blog', label: 'Blog' },
  { value: 'social', label: 'Social' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'pm', label: 'Product Manager' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'sales', label: 'Sales' },
  { value: 'strategy', label: 'Strategy Pipeline' },
  { value: 'code', label: 'Coding' },
  { value: 'image', label: 'Image' },
  { value: 'scraper', label: 'Scraper' },
  { value: 'lead_scorer', label: 'Lead Scorer' },
]

const LIMIT = 25

export default function HistoryPage() {
  const queryClient = useQueryClient()
  const [tool, setTool] = useState('')
  const [offset, setOffset] = useState(0)
  const [detailRun, setDetailRun] = useState<any>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['history', tool, offset],
    queryFn: () =>
      api.get<{ runs: any[]; total: number }>(
        `/history?limit=${LIMIT}&offset=${offset}${tool ? `&tool=${tool}` : ''}`,
      ),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/history/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['history'] }),
  })

  const [confirmNode, confirm] = useConfirm()

  const runs = data?.runs || []
  const total = data?.total || 0

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
            <Clock size={20} className="text-indigo-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Run History</h1>
        </div>
        <div className="w-48">
          <Select
            options={TOOL_OPTIONS}
            value={tool}
            onChange={(e) => { setTool(e.target.value); setOffset(0) }}
          />
        </div>
      </div>

      {confirmNode}

      {isLoading ? (
        <LoadingState message="Loading run history..." />
      ) : runs.length === 0 ? (
        <EmptyState
          icon={Clock}
          title="No runs yet"
          description="Generate content using any AI tool to see your history here"
        />
      ) : (
        <>
          <div className="space-y-2">
            {runs.map((run: any) => (
              <div
                key={run.id}
                className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4"
              >
                <div
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${run.success ? 'bg-green-500' : 'bg-red-500'}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{run.tool}</span>
                    <span className="text-xs text-gray-400 font-mono">{run.id}</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    {fmtDateTime(run.timestamp)}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setDetailRun(run)}
                    aria-label="View run details"
                    className="p-2.5 text-gray-400 hover:text-indigo-600 rounded"
                  >
                    <Eye size={15} />
                  </button>
                  <button
                    onClick={async () => { if (await confirm({ message: 'Are you sure you want to delete this run?', title: 'Delete Run', confirmLabel: 'Delete', confirmVariant: 'danger' })) deleteMutation.mutate(run.id) }}
                    aria-label="Delete run"
                    className="p-2.5 text-gray-400 hover:text-red-600 rounded"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <Pagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
        </>
      )}

      {detailRun && (
        <Modal open={true} onClose={() => setDetailRun(null)} title={`Run: ${detailRun.tool}`} size="xl">
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500">ID: </span>
                <span className="font-mono">{detailRun.id}</span>
              </div>
              <div>
                <span className="text-gray-500">Tool: </span>
                {detailRun.tool}
              </div>
              <div>
                <span className="text-gray-500">Status: </span>
                {detailRun.success ? 'Success' : 'Failed'}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Input</h4>
              <pre className="bg-gray-50 p-3 rounded-lg text-xs font-mono overflow-auto max-h-48">
                {JSON.stringify(detailRun.inputs, null, 2)}
              </pre>
            </div>
            <div>
              <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Output</h4>
              <pre className="bg-gray-50 p-3 rounded-lg text-xs font-mono overflow-auto max-h-96">
                {JSON.stringify(detailRun.result, null, 2)}
              </pre>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
